// ── Bank Feed (Stitch) — feature configuration ──
//
// This whole module is WIRED BUT DORMANT. We have no live Stitch client credentials
// wired in yet, so the bank feed must never activate. Activation requires BOTH:
//   1. BANK_FEED_ENABLED flipped to true (this file), AND
//   2. a Stitch client id present in the environment (REACT_APP_STITCH_CLIENT_ID).
// Either one missing → the feature stays hidden and every Stitch call refuses.
//
// Provider: Stitch (stitch.money) — South-African open-banking. Mono was the
// original plan but Mono does NOT support South Africa (only NG/GH/KE), so the
// provider layer was re-pointed to Stitch. Everything around it (bankFeedTransactions,
// member-read rules, Haiku categorisation, statement upload as the free fallback) is
// provider-agnostic and unchanged.

// Master switch. Leave FALSE until we actually have a Stitch client + certificate and
// have decided to go live. While false the nav item is hidden and no endpoint is hit.
export const BANK_FEED_ENABLED = false;

// Client-side Stitch client id (used only to build the OAuth authorization / Connect
// URL in the browser). The PRIVATE KEY / client certificate NEVER touches the browser —
// it lives on the server (STITCH_CLIENT_ID + STITCH_CLIENT_KEY) and signs the
// client_assertion during token exchange.
export const STITCH_CLIENT_ID = process.env.REACT_APP_STITCH_CLIENT_ID || "";

// Where Stitch sends the browser back after the account-linking consent flow. Must
// exactly match a redirect URI registered on the Stitch client.
export const STITCH_REDIRECT_URI =
  process.env.REACT_APP_STITCH_REDIRECT_URI ||
  (typeof window !== "undefined" ? window.location.origin + "/bankfeed/callback" : "");

// Scopes requested for read-only financial data (no payment scopes — we never move
// money). offline_access yields a refresh token so we can re-sync without re-linking.
export const STITCH_SCOPES = [
  "openid",
  "accounts",
  "balances",
  "transactions",
  "accountholders",
  "offline_access",
];

// True only when the feature is switched on AND a client id exists. Everything
// user-facing should gate on this so the feature can't half-activate.
export function isBankFeedEnabled() {
  return BANK_FEED_ENABLED && !!STITCH_CLIENT_ID;
}

// ── Sync cadence ──
// Per the decision doc we sync on an EVENT basis (app-open / manual refresh) with
// one guaranteed month-end sync, NOT a scheduled daily poll — monthly-like cost
// with data fresh exactly when someone is looking at it.
export const SYNC_MODE = "event"; // 'event' | 'daily' | 'weekly' | 'monthly'

// Model used for categorising the ambiguous tail of transactions (rules first,
// Claude only for the ~10–20% a merchant lookup can't resolve). Haiku keeps this
// line cheap (~R3.70/user/mo at expected volume). See categorise.js.
export const CATEGORISER_MODEL = "claude-haiku-4-5";

// Firestore locations for bank-feed state (all under the project, membership-gated
// by existing rules — meta/* is reviewers+, the rest is writers).
export const PATHS = {
  config: ["meta", "bankFeedConfig"],          // projects/{id}/meta/bankFeedConfig
  cache: ["meta", "categorisationCache"],       // projects/{id}/meta/categorisationCache
  transactions: "bankFeedTransactions",          // projects/{id}/bankFeedTransactions/{stitchTxnId}
};

// Thrown by every Stitch call while the feature is dormant, so callers fail loudly
// and clearly instead of silently hitting an unconfigured endpoint.
export class BankFeedDisabledError extends Error {
  constructor(message) {
    super(message || "The bank feed is not available yet (no Stitch client configured).");
    this.name = "BankFeedDisabledError";
  }
}
